import React, { useEffect, useState } from "react";
import { Button } from 'primereact/button';
import { InputText } from "primereact/inputtext";
import { useFormik } from 'formik';
import { classNames } from 'primereact/utils';
import { FileUpload } from 'primereact/fileupload';
import { Panel } from 'primereact/panel';
import { Tooltip } from 'primereact/tooltip';
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { send, notify } from "../../../services/NotificationService";
import {
    Form,
    redirect,
    useLoaderData,
    useNavigate,
    useNavigation,
    useSubmit
} from "react-router-dom";
import { createTool, getTool, getToolSubCategories, updateTool } from "../../../data/loca/tools";
import { getMerchandise } from "../../../data/loca/merchandise";
import { getAccount } from "../../../data/accounts";

// Comparaison sans accents ni casse pour filtrer les suggestions de sous-catégorie.
const normalizeSearchText = (text) => (text ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export async function action({ request, params }) {

    const formData = await request.formData();
    const data = Object.fromEntries(formData);
    data.warehouseIdsJson = formData.get('warehouseIdsJson') ?? "[]";

    let ret;
    if (params.toolId)
        ret =  await updateTool(data, params.toolId);
    else
        ret = await createTool(data);

    if (ret.status === 200) {
        return redirect(`/monoutil/${ret.message}`);
    } else {
        send("warning", "Une erreur est survenue. Merci de vérifier vos données ! ")
        notify();
        return null;
    }
}
export async function loader({ params }) {

    const acc = await getAccount();

    if (acc.needComplementaryInfo) {
        return redirect(`/informations-complementaires`);
    }

    if (params.toolId) {
        const [tool, merchandise, toolSubCategories] = await Promise.all([getTool(params.toolId, true), getMerchandise(), getToolSubCategories().catch(() => [])]);
        return { tool, account: acc, merchandise, toolSubCategories };
    }

    const tool = null;
    const [merchandise, toolSubCategories] = await Promise.all([getMerchandise(), getToolSubCategories().catch(() => [])]);
    return { tool, account: acc, merchandise, toolSubCategories };
}
export default function AddTool() {
    const submit = useSubmit();
    const navigate = useNavigate();
    const navigation = useNavigation();
    const { tool, account, merchandise, toolSubCategories } = useLoaderData();
    const [options, setOptions] = useState();
    const [editingOptionId, setEditingOptionId] = useState(null);
    const isSaving = navigation.state === "submitting" || navigation.state === "loading";
    const [pictures, setPictures] = useState(() => {
        if (!tool) return [];

        return [
            tool.base64Picture,
            ...((tool.pictures ?? []).map((picture) => picture.base64Picture))
        ].filter((picture) => !!picture);
    });

    useEffect(() => {

        var opts = [];
        if (tool && tool.options) {
            tool.options.forEach((item) => {
                opts.push({ Id: item.id, Name: item.name, Details: item.details, PriceCents: item.priceCents, MerchandiseItemId: item.merchandiseItemId ?? null });
            });
        }
        setOptions(opts);


    }, [tool]);


    const merchandiseOptions = [
        { name: "Aucune marchandise liée", value: null },
        ...((merchandise ?? [])
            .slice()
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "fr-FR", { sensitivity: "base" }))
            .map((item) => ({ name: `${item.name}${item.quantityInStock != null ? ` (${item.quantityInStock} en stock)` : ""}`, value: item.id })))
    ];


    let name, description, keywords, brand, model, subCategory, accessories, specificitiesUseDescription, materialReturnClause, picture, deliveryMemo, alsoAvailableInformation = "";
    let price, price2Days, price7Days, deposit, penalities, paidOptions, stockTotal, advancedPaymentPercentRequired, smallDescription, showOnHomePage, warehouseIds;
    let attributes = [];
    if (tool) {
        deliveryMemo = tool.deliveryMemo;
        alsoAvailableInformation = tool.alsoAvailableInformation;
        name = tool.name;
        description = tool.description;
        keywords = tool.keywords;
        brand = tool.brand;
        model = tool.model;
        subCategory = tool.subCategory;
        accessories = tool.accessories;
        price = tool.priceDayCents / 100;
        price2Days = tool.priceDayAfterTwoDays / 100;
        price7Days = tool.priceDayAfterSevenDays / 100;
        deposit = tool.depositCents / 100;
        penalities = tool.hourlyPenaltyCents / 100;
        advancedPaymentPercentRequired = tool.advancedPaymentPercentRequired;
        specificitiesUseDescription = tool.specificitiesUseDescription;
        materialReturnClause = tool.materialReturnClause;
        picture = tool.base64Picture;
        //base64data = tool.base64Picture;
        paidOptions = tool.paidOptions;
        stockTotal = tool.stockTotal;
        smallDescription = tool.smallDescription;
        showOnHomePage = tool.showOnHomePage;
        warehouseIds = tool.warehouseIds ?? [];
        
        
        if (tool.attributes) {
            tool.attributes.forEach((item) => {
                if (item.isSelected) {
                    attributes.push(item.name);
                }
            });
        }



    }
    // Sous-catégories déjà utilisées par le loueur (plus celle de l'outil si elle n'y figure pas). Saisie libre
    // possible : le bouton montre toute la liste, la frappe la filtre.
    const subCategoryNames = (toolSubCategories ?? []).map((option) => option.name);
    if (subCategory && !subCategoryNames.includes(subCategory)) {
        subCategoryNames.unshift(subCategory);
    }
    const [subCategorySuggestions, setSubCategorySuggestions] = useState([]);
    const searchSubCategories = (event) => {
        const query = normalizeSearchText(event.query);
        setSubCategorySuggestions(subCategoryNames.filter((name) => !query || normalizeSearchText(name).includes(query)));
    };

    // Formik ---------------------

    const formik = useFormik({
        initialValues: {
            toolname: name ?? '',
            description: description ?? '',
            keywords: keywords ?? '',
            brand: brand ?? '',
            model: model ?? '',
            subCategory: subCategory ?? '',
            accessories: accessories ?? '',
            price: price ?? '',
            price2Days: price2Days ?? '',
            price7Days: price7Days ?? '',
            deposit: deposit ?? '',
            penalities: penalities ?? '',
            advancedPaymentPercentRequired: advancedPaymentPercentRequired ?? 0,
            specificities: specificitiesUseDescription ?? '',
            materialReturnClause: materialReturnClause ?? '',
            picture: picture ?? '',
            paidOptions: paidOptions ?? '',
            attributes: attributes ?? [],
            syncToKiwiiz: true ,
            syncToAlloVoisins:  true ,
            optionName: '',
            optionDetails: '',
            optionPrice: '',
            optionMerchandiseItemId: null,
            options,
            stockTotal: stockTotal ?? 1,
            deliveryMemo: deliveryMemo ?? '',
            alsoAvailableInformation: alsoAvailableInformation ?? '',
            smallDescription: smallDescription ?? '',
            showOnHomePage: showOnHomePage ?? false,
            warehouseIds: warehouseIds ?? []

        },
        validate: (data) => {
            let errors = {};

            if (!data.toolname) {
                errors.toolname = 'toolname is required.';
            }

            if (!data.brand) {
                errors.brand = 'brand is required.';
            }

            if (!data.price) {
                errors.price = 'price is required.';
            }
            if (data.price <= 0 || isNaN(data.price)) {
                errors.price = 'enter valid price.';
            }
            if (isNaN(data.price2Days)) {
                errors.price2Days = 'enter valid price.';
            }
            if (isNaN(data.price7Days)) {
                errors.price7Days = 'enter valid price.';
            }

            if (!data.deposit) {
                errors.deposit = 'deposit is required.';
            }
            if (data.deposit <= 0 || isNaN(data.deposit)) {
                errors.deposit = 'enter valid deposit.';
            }
            if (data.deposit <= 0) {
                errors.deposit = 'enter valid deposit.';
            }
            if (!data.penalities) {
                errors.penalities = 'penalities is required.';
            }
            if (data.penalities <= 0 || isNaN(data.penalities)) {
                errors.penalities = 'enter valid penalities.';
            }
            if (data.penalities <= 0) {
                errors.penalities = 'enter valid penalities.';
            }
            if (data.advancedPaymentPercentRequired < 0 || data.advancedPaymentPercentRequired > 100 || isNaN(data.advancedPaymentPercentRequired)) {
                errors.advancedPaymentPercentRequired = 'enter valid advanced payment percent required.';
            }
            if (!data.description) {
                errors.description = 'description is required.';
            }
            if (pictures.length === 0) {
                errors.picture = 'La photo est obligatoire !';
            }
            if (!data.stockTotal || isNaN(data.stockTotal) || data.stockTotal < 1) {
                errors.stockTotal = 'enter valid stock total.';
            }

            return errors;
        },
        onSubmit: async (data) => {
            let error = false
           
            if (!error) { 
                if (pictures.length > 0) {
                    data.picture = pictures[0];
                    data.additionalPicturesJson = JSON.stringify(pictures.slice(1));
                }

                if (!tool) {
                    data.options = JSON.stringify(options);
                }
                data.warehouseIdsJson = JSON.stringify(formik.values.warehouseIds ?? []);

                submit(data, { method: "post" })
            }
        }
    });

    const isFormFieldInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldInvalid(name) ? <small className="p-error">{formik.errors[name]}</small> : <small className="p-error">&nbsp;</small>;
    };


    const resetOptionForm = () => {
        setEditingOptionId(null);
        formik.setFieldValue('optionName', '');
        formik.setFieldValue('optionDetails', '');
        formik.setFieldValue('optionPrice', '');
        formik.setFieldValue('optionMerchandiseItemId', null);
    };

     const deleteOption = (event, id) => {
        if (options) {
            var newOptions = options.filter(item => item.Id !== id);
            setOptions([...newOptions]);
            if (editingOptionId === id) {
                resetOptionForm();
            }
        }
    }

    const editOption = (option) => {
        setEditingOptionId(option.Id);
        formik.setFieldValue('optionName', option.Name ?? '');
        formik.setFieldValue('optionDetails', option.Details ?? '');
        formik.setFieldValue('optionPrice', option.PriceCents ? option.PriceCents / 100 : '');
        formik.setFieldValue('optionMerchandiseItemId', option.MerchandiseItemId ?? null);
    };

    async function addOption(event) {
        var optionName = formik.values.optionName?.trim();
        var optionDetails = formik.values.optionDetails?.trim() ?? '';
        var optionPrice = formik.values.optionPrice;

        if (!optionName) {
            send("warning", "Veuillez renseigner l'intitulé de l'option.");
            notify();
            return;
        }

        if (!optionPrice || Number(optionPrice) <= 0) {
            send("warning", "Veuillez renseigner un prix valide pour l'option.");
            notify();
            return;
        }

        const merchandiseItemId = formik.values.optionMerchandiseItemId ? Number(formik.values.optionMerchandiseItemId) : null;
        const cleanedMerchandiseItemId = Number.isNaN(merchandiseItemId) ? null : merchandiseItemId;
        const priceCents = Math.round(Number(optionPrice) * 100);

        if (editingOptionId !== null) {
            setOptions((previousOptions) => (previousOptions || []).map((option) => option.Id === editingOptionId
                ? { ...option, Name: optionName, Details: optionDetails, PriceCents: priceCents, MerchandiseItemId: cleanedMerchandiseItemId }
                : option));
            resetOptionForm();
            return;
        }

        var minId = 0;
        if (options) {
            options.forEach((item) => {
                if (item.Id < minId)
                    minId = item.Id;
            });
        }
        var id = minId - 1;

        var option = { Id: id, Name: optionName, Details: optionDetails,  PriceCents: priceCents, MerchandiseItemId: cleanedMerchandiseItemId};
        if (!options)
            setOptions([option]);
        else { // on ajoute au tableau
            options.push(option);
            setOptions([...options]);
        }

        resetOptionForm();
    }

 
    async function calculPrice() {
        var twoDays = Math.round((formik.values.price * 0.8))
        var sevenDays = Math.round((formik.values.price * 0.6))
        // si les champs sont null on set la valeur
        if (!formik.values.price2Days)
            formik.setFieldValue('price2Days', twoDays);
        if (!formik.values.price7Days)
            formik.setFieldValue('price7Days', sevenDays);
    }

    const readFileAsDataUrl = async (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const customPicturesUploader = async (event) => {
        const files = event.files ?? [];
        const uploadedPictures = await Promise.all(files.map(async (file) => {
            const blob = file.objectURL ? await fetch(file.objectURL).then((response) => response.blob()) : file;
            return await readFileAsDataUrl(blob);
        }));

        setPictures((previousPictures) => [
            ...previousPictures,
            ...uploadedPictures.filter((picture) => !!picture)
        ]);
    };

    const removePicture = (index) => {
        setPictures((previousPictures) => previousPictures.filter((_, pictureIndex) => pictureIndex !== index));
    };

    const setPrimaryPicture = (index) => {
        setPictures((previousPictures) => {
            if (index <= 0 || index >= previousPictures.length) return previousPictures;

            const nextPictures = [...previousPictures];
            const [selectedPicture] = nextPictures.splice(index, 1);
            return [selectedPicture, ...nextPictures];
        });
    };
    

    return (
        <div>

            <h1>{(tool) ? "Modification" : "Ajout"} d'un outil</h1>
            <Form method="post" id="thumbnail-form" enctype="multipart/form-data" className="flex flex-column gap-2" onSubmit={formik.handleSubmit}>

                <Panel header="D&eacute;tails">
                    <div className="contentForm">
                        <span className="p-float-label">
                            <InputText id="toolname" name="toolname" value={formik.values.toolname} data-cy="toolname"
                                onChange={(e) => {
                                    formik.setFieldValue('toolname', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('toolname') })}
                            />

                            <label htmlFor="toolname">Nom</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="brand" name="brand" value={formik.values.brand}  data-cy="brand"
                                onChange={(e) => {
                                    formik.setFieldValue('brand', e.target.value);
                                }}
                               
                                className={classNames({ 'p-invalid': isFormFieldInvalid('brand') })}
                            />
                            <label htmlFor="brand">Marque</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="model" name="model" value={formik.values.model} data-cy="model"
                                onChange={(e) => {
                                    formik.setFieldValue('model', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('model') })}
                            />
                            <label htmlFor="model">Mod&egrave;le</label>
                        </span>

                        <span className="p-float-label">
                            <AutoComplete inputId="subCategory" value={formik.values.subCategory ?? ''}
                                suggestions={subCategorySuggestions} completeMethod={searchSubCategories} dropdown
                                onChange={(e) => {
                                    formik.setFieldValue('subCategory', e.value ?? '');
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('subCategory') })}
                            />
                            <label htmlFor="subCategory">Sous-cat&eacute;gorie</label>
                        </span>

                        
                    </div >
                  
                    <div className="contentForm">


                        <Tooltip target=".accessories-tooltip" >
                            <div className="card">Ex: <u>Article 1 : Objet de la location</u></div>
                            <div className="card">avec les accessoires suivants : <i>"clé de démontage, embout aspirateur, sac anti-poussière, coffret de transport"</i></div>
                        </Tooltip>
                        <span className="p-float-label accessories-tooltip">
                            <InputText id="accessories" name="accessories" value={formik.values.accessories}
                                onChange={(e) => {
                                    formik.setFieldValue('accessories', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('accessories') })}
                            />
                            <label htmlFor="accessories">Accessoires</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="stockTotal" name="stockTotal" value={formik.values.stockTotal} data-cy="stockTotal"
                                keyfilter="money"
                                onChange={(e) => {
                                    formik.setFieldValue('stockTotal', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('stockTotal') })}
                            />
                            <label htmlFor="stockTotal">Stock total</label>
                        </span>
                         
                    </div >
                    <div className="contentForm">
                        <span className="p-float-label" >

                            <InputTextarea id="description" value={formik.values.description}
                                onChange={(e) => {
                                    formik.setFieldValue('description', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('description') })}
                                rows={5} cols={30} />
                            <label htmlFor="description">Description</label>
                        </span>
                    </div >
                    <div className="contentForm">

                        <span className="p-float-label" >


                            <InputTextarea id="smallDescription" value={formik.values.smallDescription}
                                onChange={(e) => {
                                    formik.setFieldValue('smallDescription', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('smallDescription') })}
                                rows={2} cols={10} />
                            <label htmlFor="smallDescription">Résumé</label>
                        </span>
                    </div >
                    <div className="contentForm">

                        <span className="p-float-label" >


                            <InputTextarea id="keywords" value={formik.values.keywords}
                                onChange={(e) => {
                                    formik.setFieldValue('keywords', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('keywords') })}
                                rows={1} cols={10} />
                            <label htmlFor="keywords">Mots-clés</label>
                        </span>
                    </div >
                    <div className="contentForm">

                        <span className="p-float-label" >


                            <InputTextarea id="deliveryMemo" value={formik.values.deliveryMemo}
                                onChange={(e) => {
                                    formik.setFieldValue('deliveryMemo', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('deliveryMemo') })}
                                rows={2} cols={10} />
                            <label htmlFor="deliveryMemo">Notes, si livraison/installation</label>
                        </span>
                    </div >
                    <div className="contentForm">
                        <span className="p-float-label" >
                            <InputTextarea id="alsoAvailableInformation" value={formik.values.alsoAvailableInformation}
                                onChange={(e) => {
                                    formik.setFieldValue('alsoAvailableInformation', e.target.value);
                                }}
                                rows={2} cols={10} />
                            <label htmlFor="alsoAvailableInformation">Également disponible (message AlloVoisins)</label>
                        </span>
                        <small className="tool-form-help">Remplace [EGALEMENT_DISPONIBLE] dans le message envoyé aux demandes AlloVoisins, ex. « Je loue aussi une remorque et un taille-haie. »</small>
                    </div >
                    <div className="contentForm"></div >
                </Panel>




                <Panel header="Lieux de retrait">
                    <input type="hidden" name="warehouseIdsJson" value={JSON.stringify(formik.values.warehouseIds ?? [])} />
                    {(account?.warehouses ?? []).length === 0 ? (
                        <p>Aucun entrepôt sélectionnable. L'adresse principale sera utilisée.</p>
                    ) : (
                        <div className="warehouse-selector">
                            <div className="warehouse-selector__header">
                                <b>Entrepôts disponibles</b>
                                <span>Vous pouvez en sélectionner plusieurs pour un même outil.</span>
                            </div>
                            <div className="warehouse-selector__grid">
                                {(account?.warehouses ?? []).map((warehouse) => {
                                    const checked = (formik.values.warehouseIds ?? []).includes(String(warehouse.id)) || (formik.values.warehouseIds ?? []).includes(warehouse.id);
                                    return (
                                        <label
                                            key={warehouse.id}
                                            htmlFor={`warehouse-${warehouse.id}`}
                                            className={classNames('warehouse-selector__option', { 'is-selected': checked })}
                                        >
                                            <input
                                                id={`warehouse-${warehouse.id}`}
                                                type="checkbox"
                                                className="warehouse-selector__checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const current = (formik.values.warehouseIds ?? []).map(String);
                                                    const next = e.target.checked
                                                        ? [...current, String(warehouse.id)]
                                                        : current.filter((item) => item !== String(warehouse.id));
                                                    formik.setFieldValue('warehouseIds', next);
                                                }}
                                            />
                                            <div className="warehouse-selector__content">
                                                <span className="warehouse-selector__badge">{checked ? 'Sélectionné' : 'Sélectionner'}</span>
                                                <span className="warehouse-selector__name">{warehouse.name}</span>
                                                <span className="warehouse-selector__meta">{warehouse.zipcode ? `${warehouse.zipcode} ` : ''}{warehouse.city}</span>
                                                <span className="warehouse-selector__address">{warehouse.address || 'Adresse non renseignée'}</span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            <small className="warehouse-selector__hint">Si aucun entrepôt n'est coché, l'adresse principale du compte restera utilisée publiquement.</small>
                        </div>
                    )}
                </Panel>

                <Panel header="Informations financi&egrave;res" >
                    <div className="contentForm">
                        <span className="p-float-label">
                            <InputText id="price" name="price" value={formik.values.price} data-cy="price"
                                keyfilter="money"
                                onBlur={() => calculPrice()}
                                onChange={(e) => {
                                    formik.setFieldValue('price', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('price') })}
                            />
                            <label htmlFor="price">Prix/j</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="price" name="price2Days" value={formik.values.price2Days} data-cy="price2Days"
                                keyfilter="money"
                                onChange={(e) => {
                                    formik.setFieldValue('price2Days', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('price2Days') })}
                            />
                            <label htmlFor="price">Prix/j à partir de 2j</label>
                        </span>
                        <span className="p-float-label">
                            <InputText id="price" name="price7Days" value={formik.values.price7Days} data-cy="price7Days"
                                keyfilter="money"
                                onChange={(e) => {
                                    formik.setFieldValue('price7Days', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('price7Days') })}
                            />
                            <label htmlFor="price">Prix/j partir de 7j</label>
                        </span>

                    </div>
                    <div className="contentForm">
                        <span className="p-float-label">
                            <InputText id="deposit" name="deposit" value={formik.values.deposit} data-cy="deposit"
                                keyfilter="money"
                                onChange={(e) => {
                                    formik.setFieldValue('deposit', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('deposit') })}
                            />
                            <label htmlFor="deposit">Caution</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="penalities" name="penalities" value={formik.values.penalities} data-cy="penalities"
                                keyfilter="money"
                                onChange={(e) => {
                                    formik.setFieldValue('penalities', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('penalities') })}
                            />
                            <label htmlFor="penalities">Pénalités/h de retard</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="advancedPaymentPercentRequired" name="advancedPaymentPercentRequired" value={formik.values.advancedPaymentPercentRequired} data-cy="advancedPaymentPercentRequired"
                                keyfilter="num"
                                onChange={(e) => {
                                    formik.setFieldValue('advancedPaymentPercentRequired', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('advancedPaymentPercentRequired') })}
                            />
                            <label htmlFor="advancedPaymentPercentRequired">Pourcentage d'acompte</label>
                        </span>

                    </div>
                    
                    <div className="contentForm"></div >
                </Panel>




                <Panel header="Informations contractuelles" >
                    <div id="contractPanel">
                        <div className="contentForm">

                            <Tooltip target=".specificities-tooltip" >
                                <div className="card">Ex: <u>Article 5 : Utilisation du matériel </u></div>
                                <div className="card"><i>"Si le locataire devait remplir le réservoir, UN MÉLANGE 2 TEMPS est requis. Si un autre mélange venait à être mis dans l’appareil, une partie ou la totalité de la caution pourra être retenue."</i></div>
                            </Tooltip>
                            <span className="p-float-label  specificities-tooltip" >


                                <InputTextarea id="specificities" value={formik.values.specificities}
                                    onChange={(e) => {
                                        formik.setFieldValue('specificities', e.target.value);
                                    }}
                                    className={classNames({ 'p-invalid': isFormFieldInvalid('specificities') })}
                                    rows={5} cols={30} />
                                <label htmlFor="specificities">Clause d'utilisation sp&eacute;cifique</label>
                            </span>

                        </div>

                        <div className="contentForm">
                            <Tooltip target=".paidOptions-tooltip" >
                                <div className="card">Ex: <u>Article 3 : Montant de la location </u></div>
                                <div className="card"><i>"Le locataire devra régler sa consommation de gaz. A savoir : 0.5€/Bar d'oxygène et 8€/Bar d'acétylène"</i></div>
                            </Tooltip>
                            <span className="p-float-label paidOptions-tooltip">


                                <InputTextarea id="paidOptions" value={formik.values.paidOptions}
                                    onChange={(e) => {
                                        formik.setFieldValue('paidOptions', e.target.value);
                                    }}
                                    className={classNames({ 'p-invalid': isFormFieldInvalid('paidOptions') })}
                                    rows={5} cols={30} />
                                <label htmlFor="paidOptions">Consommation(s) payante(s) au retour du matériel</label>
                            </span>

                        </div>
                        <div className="contentForm">

                            <Tooltip target=".materialReturnClause-tooltip" >
                                <div className="card">Ex: <u>Article 6 : Restitution du matériel </u></div>
                                <div className="card"><i>"... A titre d'information, les frais de nettoyage seront facturés 10€"</i></div>
                            </Tooltip>
                            <span className="p-float-label  materialReturnClause-tooltip" >


                                <InputTextarea id="materialReturnClause" value={formik.values.materialReturnClause}
                                    onChange={(e) => {
                                        formik.setFieldValue('materialReturnClause', e.target.value);
                                    }}
                                    className={classNames({ 'p-invalid': isFormFieldInvalid('materialReturnClause') })}
                                    rows={5} cols={30} />
                                <label htmlFor="materialReturnClause">Clause de réstitution de matériel</label>
                            </span>

                        </div>

                    </div>

                    


                    <div className="contentForm"></div >
                </Panel>



                {!tool && (
                <Panel header="Options payantes" >


                    <div className="contentForm">
                        <label >Définissez une liste d'options payantes possible lors de la location de votre outil</label>
                    </div>

                    <div className="contentForm">


                        <span className="p-float-label">
                            <InputText id="optionName" name="optionName" value={formik.values.optionName} data-cy="optionName"
                                onChange={(e) => {
                                    formik.setFieldValue('optionName', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('optionName') })}
                            />

                            <label htmlFor="toolname">Intitulé de l'option</label>
                        </span>

                        <span className="p-float-label">
                            <InputText id="optionDetails" name="optionDetails" value={formik.values.optionDetails} data-cy="optionDetails"
            
                                onChange={(e) => {
                                    formik.setFieldValue('optionDetails', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('optionDetails') })}
                            />
                            <label htmlFor="brand">Détails</label>
                        </span>

                      



                        <span className="p-float-label">
                            <InputText id="optionPrice" name="optionPrice" value={formik.values.optionPrice} data-cy="optionPrice"
                                keyfilter="money"
                                onChange={(e) => {
                                    formik.setFieldValue('optionPrice', e.target.value);
                                }}
                                className={classNames({ 'p-invalid': isFormFieldInvalid('optionPrice') })}
                            />
                            <label htmlFor="optionPrice">Prix</label>
                        </span>

                        <span className="p-float-label">
                            <Dropdown id="optionMerchandiseItemId" name="optionMerchandiseItemId" value={formik.values.optionMerchandiseItemId}
                                options={merchandiseOptions}
                                optionLabel="name"
                                optionValue="value"
                                showClear
                                filter
                                onChange={(e) => {
                                    const selectedMerchandiseId = e.value ?? null;
                                    formik.setFieldValue('optionMerchandiseItemId', selectedMerchandiseId);

                                    if (selectedMerchandiseId == null) {
                                        return;
                                    }

                                    const selectedMerchandise = merchandise.find((item) => item.id === selectedMerchandiseId);
                                    if (!selectedMerchandise) {
                                        return;
                                    }

                                    formik.setFieldValue('optionName', selectedMerchandise.name ?? '');
                                    formik.setFieldValue('optionDetails', selectedMerchandise.name ?? '');
                                    formik.setFieldValue('optionPrice', selectedMerchandise.suggestedRetailPrice != null ? selectedMerchandise.suggestedRetailPrice.toString() : '');
                                }} />
                            <label htmlFor="optionMerchandiseItemId">Marchandise liée</label>
                        </span>

                        <Button onClick={() => addOption()} type="button" size="small" icon={editingOptionId !== null ? "pi pi-check" : "pi pi-plus"} label={editingOptionId !== null ? "Modifier" : undefined}></Button>
                        {editingOptionId !== null && (
                            <Button onClick={() => resetOptionForm()} type="button" size="small" icon="pi pi-times" text severity="secondary" />
                        )}
                    </div >
                    
                    <div className="contentForm"></div >

                    {(options && options.length > 0) &&
                        (
                        <div className="contentForm">
                            <table id="optionsTable">
                                <thead>
                                    <tr>
                                        <th>Intitulé</th>
                                        <th>Détails</th>
                                        <th>Prix</th>
                                        <th>Marchandise liée</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody> 
                                    {options.map((opt) => (
                                        <tr key={opt.Id}>
                                            <td>{opt.Name}</td>
                                            <td>{opt.Details}</td>
                                            <td>{opt.PriceCents / 100}</td>
                                            <td>{merchandise.find((item) => item.id === opt.MerchandiseItemId)?.name || "—"}</td>
                                            <td>
                                                <Button onClick={() => editOption(opt)} icon="pi pi-pencil" size="small" rounded text aria-label="Modifier" />
                                                <Button onClick={(e) => deleteOption(e, opt.Id)} icon="pi pi-times" size="small" rounded text severity="danger" aria-label="Cancel" />

                                            </td>
                                        </tr>
                                         ))}
                                </tbody>
                            </table>
                        </div >
                        
                        )
                    }
                    

                    <div className="contentForm"></div >
                </Panel>
                )}


               

                <div className="card tool-picture-uploader">
                    <div className="tool-picture-uploader__header">
                        <div>
                            <h2>Photos du matériel</h2>
                            <p>La première photo sera utilisée comme photo principale dans les listes et sur la page publique.</p>
                        </div>
                    </div>
                    {pictures.length > 0 && (
                        <div className="tool-picture-grid">
                            {pictures.map((pictureItem, index) => (
                                <div className={classNames("tool-picture-item", { "tool-picture-item--primary": index === 0 })} key={`${pictureItem}-${index}`}>
                                    <img src={pictureItem} alt={`${formik.values.toolname || "Matériel"} ${index + 1}`} />
                                    <div className="tool-picture-item__actions">
                                        {index === 0 ? (
                                            <span>Principale</span>
                                        ) : (
                                            <Button type="button" size="small" text icon="pi pi-star" onClick={() => setPrimaryPicture(index)} aria-label="Mettre en photo principale" />
                                        )}
                                        <Button type="button" size="small" text severity="danger" icon="pi pi-trash" onClick={() => removePicture(index)} aria-label="Supprimer la photo" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <FileUpload name="picture[]" className={classNames({ 'p-invalid': isFormFieldInvalid('picture') })} accept="image/*" maxFileSize={5000000} multiple auto uploadHandler={customPicturesUploader} customUpload emptyTemplate={<p className="m-0">Déposez vos images ici</p>} chooseLabel="Ajouter des photos" />
                    {getFormErrorMessage('picture')}
                </div>

                
           
            <p>
                    <Button type="submit" size="small" data-cy="saveBtn" loading={isSaving} disabled={isSaving}>Enregistrer</Button>
                <Button type="button"
                    size="small"
                    severity="secondary"
                    disabled={isSaving}
                    onClick={() => {
                        navigate(-1);
                    }}>Annuler</Button>
            </p>
            </Form>
            </div>
    );
}
